import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMutateNotification } from '@/config/communication/notification/mutate';
import { cn } from '@/lib/cn';
import { toReadableDateTime } from '@/lib/dates';
import { type Notification } from '@pigello/pigello-matrix';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { getRedirectUrl } from './utils';

export function Notification({
  notification,
  setSheetOpen,
}: {
  notification: Notification;
  setSheetOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const res = await getRedirectUrl(notification.id);
      return { redirectUrl: res.redirectUrl };
    },
    onSuccess: ({ redirectUrl }) => {
      markAsRead({
        id: notification.id,
        body: { read: true },
      });
      if (redirectUrl) {
        router.push(redirectUrl);
        setSheetOpen(false);
      }
    },
    onError: () => {
      toast.error('Misslyckades med att hämta notisen');
    },
  });
  const { mutate: markAsRead } = useMutateNotification({
    onError: () => {
      toast.error('Misslyckades med att markera som läst');
    },
  });

  return (
    <div
      tabIndex={0}
      role='button'
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isPending) {
          mutate();
        }
      }}
      onClick={() => {
        if (!isPending) {
          mutate();
        }
      }}
    >
      <div
        className={cn(
          'group flex items-center gap-2 rounded-md p-2 text-left transition-colors hover:bg-accent',
          notification.read && 'opacity-70'
        )}
      >
        <div className='flex w-full items-center justify-between gap-2'>
          <div className='flex flex-col gap-0.5'>
            <p className='font-medium'>{notification.title}</p>
            <p>
              {notification.content.length > 50 && !expanded
                ? `${notification.content.substring(0, 50)}...`
                : notification.content}
            </p>
            {notification.content.length > 50 && (
              <Button
                size='sm'
                variant='link'
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                className='mr-auto p-0'
              >
                {!expanded ? 'Visa mer' : 'Visa mindre'}
              </Button>
            )}
            <p className='w-full text-xs text-muted-foreground'>
              {toReadableDateTime(notification.createdAt)}
            </p>
          </div>

          <div className='flex shrink-0 items-center justify-start gap-2'>
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  className='invisible rounded-full group-hover:visible focus-within:visible data-[state=open]:visible'
                  size='icon'
                  variant='ghost'
                >
                  <Icons.moreOptions className='size-7' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={(e) => {
                    markAsRead({
                      id: notification.id,
                      body: { read: !notification.read },
                    });
                    setOpen(false);
                    e.stopPropagation();
                  }}
                >
                  Markera som {notification.read ? 'oläst' : 'läst'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div
              className={cn(
                'size-2.5 rounded-full',
                notification.read ? 'bg-transparent' : 'bg-primary'
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
